import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import {
  GoogleAuthProvider,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  type ConfirmationResult,
} from "firebase/auth";
import { useAuth } from "./AuthProvider";
import { getFirebaseAuth } from "../lib/firebase";
import { Logo } from "../components/Logo";
import { getFamilyConfigForHost } from "../lib/families";

/** Fire-and-forget: report auth failures to the API so they show up in Slack. */
function reportAuthDebug(step: string, error: string, extra?: { email?: string; phone?: string }) {
  const apiBase = import.meta.env.VITE_API_URL ?? "/api";
  fetch(`${apiBase}/auth-debug`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ step, error, ...extra }),
  }).catch(() => {});
}

// Branded palette for the landing panel. Kept local (not global theme
// tokens) so it stays warm/cream regardless of the app's light/dark mode.
const LAND = {
  cream: "#f4f1ea",
  green: "#213d28",
  greenSoft: "#5a6e5c",
  body: "#4a4a46",
  gold: "#b08d4a",
};

const LANDING_FEATURES: Array<[string, string]> = [
  ["Family tree", "see how everyone connects"],
  ["Photos & memories", "share moments, tag people"],
  ["Family map", "where we come from and are now"],
  ["Events & news", "birthdays, gatherings, updates"],
  ["Family AI", "ask about our history & ties"],
];

export function LoginPage() {
  const { state } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [confirmation, setConfirmation] = useState<ConfirmationResult | null>(null);
  const [busy, setBusy] = useState(false);
  const verifierRef = useRef<RecaptchaVerifier | null>(null);

  useEffect(() => {
    return () => {
      verifierRef.current?.clear();
      verifierRef.current = null;
    };
  }, []);

  // Handle redirect result (for Safari/iOS fallback). This hook must stay
  // above the conditional returns below: when sign-in completes, the
  // signed-in return short-circuits the render, and a hook declared after
  // it would make React see fewer hooks than the previous render and
  // crash — with no ErrorBoundary that meant a blank page after login.
  const authConfigured = state.status !== "unconfigured";
  useEffect(() => {
    if (!authConfigured) return;
    const auth = getFirebaseAuth();
    getRedirectResult(auth).catch((e) => {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg && !msg.includes("No redirect result")) {
        setError(msg);
        reportAuthDebug("google-redirect-result", msg);
      }
    });
  }, [authConfigured]);

  // Resolve post-sign-in destination. Precedence:
  //   1. ?from=/some/path on the login URL
  //   2. location.state.from.pathname (set by RequireAuth redirect)
  //   3. "/"
  const fromQuery = searchParams.get("from");
  const fromState = (location.state as { from?: { pathname?: string } } | null)
    ?.from?.pathname;
  const redirectTo = fromQuery ?? fromState ?? "/";

  if (state.status === "signed-in") return <Navigate to={redirectTo} replace />;

  if (state.status === "unconfigured") {
    return (
      <div className="page--auth">
        <div className="page--auth__card stack">
          <div className="page--auth__logo">
            <Logo variant="on-light" size={120} />
          </div>
          <p className="text-muted">
            Firebase isn't configured yet. Copy{" "}
            <code>apps/web/.env.example</code> to <code>.env.local</code> and
            fill in the Firebase web config, then restart{" "}
            <code>pnpm dev:web</code>.
          </p>
        </div>
      </div>
    );
  }

  async function handleGoogle() {
    setBusy(true);
    setError(null);
    try {
      const auth = getFirebaseAuth();
      // Home-screen web apps (iOS "Add to Home Screen", installed PWAs) can't
      // complete popup sign-in: the popup's link back to the app is severed,
      // so the result never arrives and the tap looks like a silent no-op.
      // Use the full-page redirect flow there from the start.
      const isStandaloneApp =
        window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
        (navigator as { standalone?: boolean }).standalone === true;
      if (isStandaloneApp) {
        await signInWithRedirect(auth, new GoogleAuthProvider());
        return; // page will redirect
      }
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // If the popup was blocked, closed, or unsupported in this environment,
      // fall back to the redirect flow.
      const isPopupBlocked =
        msg.includes("popup-closed-by-user") ||
        msg.includes("popup-blocked") ||
        msg.includes("cancelled-popup-request") ||
        msg.includes("operation-not-supported-in-this-environment") ||
        msg.includes("web-storage-unsupported");
      if (isPopupBlocked) {
        try {
          const auth = getFirebaseAuth();
          await signInWithRedirect(auth, new GoogleAuthProvider());
          return; // page will redirect
        } catch (e2) {
          const msg2 = e2 instanceof Error ? e2.message : String(e2);
          setError(msg2);
          reportAuthDebug("google-redirect", msg2);
        }
      } else {
        setError(msg);
        reportAuthDebug("google-popup", msg);
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleSendCode() {
    setBusy(true);
    setError(null);
    try {
      const auth = getFirebaseAuth();
      if (!verifierRef.current) {
        verifierRef.current = new RecaptchaVerifier(auth, "recaptcha-container", {
          size: "invisible",
        });
        await verifierRef.current.render();
      }
      const result = await signInWithPhoneNumber(auth, phone, verifierRef.current);
      setConfirmation(result);
    } catch (e) {
      verifierRef.current?.clear();
      verifierRef.current = null;
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      reportAuthDebug("send-sms-code", msg, { phone });
    } finally {
      setBusy(false);
    }
  }

  async function handleVerifyCode() {
    if (!confirmation) return;
    setBusy(true);
    setError(null);
    try {
      await confirmation.confirm(code);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      reportAuthDebug("verify-sms-code", msg, { phone });
    } finally {
      setBusy(false);
    }
  }

  const family = getFamilyConfigForHost();
  const familyName = family?.name ?? null;
  const hero = family?.hero ?? null;

  const primaryBtn: CSSProperties = {
    padding: "12px 16px",
    background: LAND.green,
    color: LAND.cream,
    border: "none",
    borderRadius: 8,
    fontWeight: 700,
    fontSize: "0.95rem",
    cursor: "pointer",
  };
  const secondaryBtn: CSSProperties = {
    padding: "11px 16px",
    background: "transparent",
    color: LAND.green,
    border: `1.5px solid ${LAND.green}`,
    borderRadius: 8,
    fontWeight: 600,
    fontSize: "0.95rem",
    cursor: "pointer",
  };
  const landInput: CSSProperties = {
    padding: "11px 12px",
    border: "1px solid rgba(33,61,40,0.3)",
    borderRadius: 8,
    background: "#fff",
    color: LAND.green,
    fontSize: "0.95rem",
    width: "100%",
  };
  const busyStyle: CSSProperties = busy
    ? { opacity: 0.6, cursor: "default" }
    : {};

  return (
    <div className="landing">
      <div
        className="landing__hero"
        aria-hidden="true"
        style={{ background: LAND.green }}
      >
        {hero && (
          <img
            src={hero}
            alt=""
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        )}
      </div>
      <div
        className="landing__content"
        style={{ background: LAND.cream, color: LAND.green }}
      >
        <div className="landing__inner">
        <div style={{ marginBottom: 4 }}>
          <Logo variant="on-light" size={120} />
        </div>

        <h1
          style={{
            fontFamily: "Georgia, 'Times New Roman', serif",
            fontSize: "2rem",
            lineHeight: 1.15,
            margin: 0,
            color: LAND.green,
          }}
        >
          {familyName ? `The ${familyName} family,` : "Your family,"}
          <br />
          all in one place.
        </h1>

        <p
          style={{
            fontFamily: "Georgia, 'Times New Roman', serif",
            fontStyle: "italic",
            color: LAND.greenSoft,
            margin: 0,
          }}
        >
          Rooted in our history · connected by love · united across the world.
        </p>

        <p style={{ color: LAND.body, margin: "4px 0", lineHeight: 1.5 }}>
          {familyName ?? "This"} is a private home online, just for our family —
          where we keep our family tree, share photos and memories, and stay
          close no matter where in the world we are. Family only. No ads, no
          strangers.
        </p>

        <ul className="landing__features">
          {LANDING_FEATURES.map(([label, rest]) => (
            <li
              key={label}
              style={{
                display: "flex",
                gap: 8,
                alignItems: "baseline",
                color: LAND.body,
                fontSize: "0.92rem",
              }}
            >
              <span style={{ color: LAND.gold }}>•</span>
              <span>
                <strong style={{ color: LAND.green }}>{label}</strong> — {rest}
              </span>
            </li>
          ))}
        </ul>

        {/* --- Sign-in (all original auth logic preserved) --- */}
        <div className="stack" style={{ marginTop: 6 }}>
          <button
            onClick={handleGoogle}
            disabled={busy}
            style={{ ...primaryBtn, ...busyStyle }}
          >
            Continue with Google
          </button>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: LAND.greenSoft,
              fontSize: "0.82rem",
              margin: "2px 0",
            }}
          >
            <span style={{ flex: 1, height: 1, background: "rgba(33,61,40,0.15)" }} />
            or
            <span style={{ flex: 1, height: 1, background: "rgba(33,61,40,0.15)" }} />
          </div>

          {!confirmation ? (
            <div className="stack">
              <input
                aria-label="Phone number with country code"
                type="tel"
                placeholder="Phone (with country code, e.g. +46…)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={busy}
                style={landInput}
              />
              <button
                onClick={handleSendCode}
                disabled={busy || !phone}
                style={{ ...secondaryBtn, ...busyStyle }}
              >
                Send code via SMS
              </button>
            </div>
          ) : (
            <div className="stack">
              <input
                aria-label="6-digit verification code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={busy}
                style={landInput}
              />
              <button
                onClick={handleVerifyCode}
                disabled={busy || code.length !== 6}
                style={{ ...primaryBtn, ...busyStyle }}
              >
                Verify
              </button>
            </div>
          )}

          <div id="recaptcha-container" />

          {error && (
            <p style={{ color: "#b00020", fontSize: "0.85rem", margin: 0 }}>
              {error}
            </p>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
